import React, { useState } from 'react'
import {
  XMarkIcon,
  QrCodeIcon,
  ClipboardDocumentIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import { paymentGatewayAPI } from '@/services/api'

interface CreatePixOrderModalProps {
  isOpen: boolean
  onClose: () => void
  gatewayId: string
  onSuccess?: () => void
}

interface OrderResult {
  order_id: string
  pix_qr_code: string
  pix_qr_code_url: string
  pix_code: string
  expires_at: string
  status: string
}

const CreatePixOrderModal: React.FC<CreatePixOrderModalProps> = ({
  isOpen,
  onClose,
  gatewayId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null)
  const [copied, setCopied] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    // Customer data
    customer_name: '',
    customer_email: '',
    customer_document: '',
    customer_type: 'individual',
    customer_phone_area: '',
    customer_phone_number: '',
    customer_address_line: '',
    customer_address_zip: '',
    customer_address_city: '',
    customer_address_state: '',

    // Item data
    item_description: '',
    item_amount: '', // Em reais
    item_quantity: '1',

    // PIX config
    expires_in: '86400' // 24 horas em segundos
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)

      // Converter valor de reais para centavos
      const amountInCents = Math.round(parseFloat(formData.item_amount) * 100)
      const quantity = parseInt(formData.item_quantity)

      // Preparar dados do pedido
      const orderData = {
        amount: amountInCents * quantity, // Total em centavos
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_document: formData.customer_document.replace(/\D/g, ''), // Remove caracteres não numéricos
        customer_type: formData.customer_type,
        customer_phone_country: '55',
        customer_phone_area: formData.customer_phone_area,
        customer_phone_number: formData.customer_phone_number,
        customer_address_line: formData.customer_address_line,
        customer_address_zip: formData.customer_address_zip.replace(/\D/g, ''),
        customer_address_city: formData.customer_address_city,
        customer_address_state: formData.customer_address_state,
        customer_address_country: 'BR',
        items: [
          {
            description: formData.item_description,
            amount: amountInCents, // Valor unitário em centavos
            quantity: quantity
          }
        ],
        expires_in: parseInt(formData.expires_in)
      }

      console.log('📤 Enviando pedido PIX:', orderData)

      const result = await paymentGatewayAPI.createOrderPix(gatewayId, orderData)

      console.log('✅ Pedido PIX criado:', result)

      setOrderResult(result)

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar pedido PIX:', error)
      alert('Erro ao criar pedido PIX: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const copyPixCode = () => {
    if (orderResult) {
      navigator.clipboard.writeText(orderResult.pix_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  const handleClose = () => {
    setOrderResult(null)
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_document: '',
      customer_type: 'individual',
      customer_phone_area: '',
      customer_phone_number: '',
      customer_address_line: '',
      customer_address_zip: '',
      customer_address_city: '',
      customer_address_state: '',
      item_description: '',
      item_amount: '',
      item_quantity: '1',
      expires_in: '86400'
    })
    onClose()
  }

  if (!isOpen) return null

  // Se já temos resultado, mostra o QR code
  if (orderResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <QrCodeIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Pedido PIX Criado!</h2>
                  <p className="text-sm text-gray-500">ID: {orderResult.order_id}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* QR Code Image */}
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg border-2 border-green-500">
                <img
                  src={`data:image/png;base64,${orderResult.pix_qr_code}`}
                  alt="QR Code PIX"
                  className="w-64 h-64"
                />
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">
                Escaneie o QR Code para pagar
              </p>
            </div>

            {/* PIX Copy-Paste Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código PIX Copia e Cola
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={orderResult.pix_code}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                />
                <button
                  onClick={copyPixCode}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <CheckIcon className="w-5 h-5" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="w-5 h-5" />
                      Copiar
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-green-700">{orderResult.status}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Expira em:</span>
                <span className="font-semibold text-gray-900">
                  {new Date(orderResult.expires_at).toLocaleString('pt-BR')}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
              <a
                href={orderResult.pix_qr_code_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-center"
              >
                Ver QR Code
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Form para criar pedido
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Criar Pedido PIX</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Dados do Cliente */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="João da Silva"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="joao@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF/CNPJ *
                </label>
                <input
                  type="text"
                  name="customer_document"
                  value={formData.customer_document}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="000.000.000-00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DDD *
                </label>
                <input
                  type="text"
                  name="customer_phone_area"
                  value={formData.customer_phone_area}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="11"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone *
                </label>
                <input
                  type="text"
                  name="customer_phone_number"
                  value={formData.customer_phone_number}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="999999999"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço *
                </label>
                <input
                  type="text"
                  name="customer_address_line"
                  value={formData.customer_address_line}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Rua Exemplo, 123"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CEP *
                </label>
                <input
                  type="text"
                  name="customer_address_zip"
                  value={formData.customer_address_zip}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="00000-000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cidade *
                </label>
                <input
                  type="text"
                  name="customer_address_city"
                  value={formData.customer_address_city}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="São Paulo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado *
                </label>
                <input
                  type="text"
                  name="customer_address_state"
                  value={formData.customer_address_state}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="SP"
                />
              </div>
            </div>
          </div>

          {/* Dados do Item */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Item / Produto</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  name="item_description"
                  value={formData.item_description}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ex: Produto Premium"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Unitário (R$) *
                </label>
                <input
                  type="number"
                  name="item_amount"
                  value={formData.item_amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="29.90"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade *
                </label>
                <input
                  type="number"
                  name="item_quantity"
                  value={formData.item_quantity}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total
                </label>
                <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg font-semibold text-gray-900">
                  {formData.item_amount && formData.item_quantity
                    ? `R$ ${(parseFloat(formData.item_amount) * parseInt(formData.item_quantity)).toFixed(2)}`
                    : 'R$ 0,00'}
                </div>
              </div>
            </div>
          </div>

          {/* Configurações PIX */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações PIX</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tempo de Expiração
                </label>
                <select
                  name="expires_in"
                  value={formData.expires_in}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="3600">1 hora</option>
                  <option value="21600">6 horas</option>
                  <option value="43200">12 horas</option>
                  <option value="86400">24 horas (padrão)</option>
                  <option value="172800">48 horas</option>
                </select>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Criando...
                </>
              ) : (
                <>
                  <QrCodeIcon className="w-5 h-5" />
                  Gerar PIX
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreatePixOrderModal
